import _regeneratorRuntime from "@babel/runtime/regenerator";
import _asyncToGenerator from "@babel/runtime/helpers/esm/asyncToGenerator";
import _objectSpread from "@babel/runtime/helpers/esm/objectSpread2";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import { useEffect, useImperativeHandle, useRef, useState } from 'react';
import staticConfig from './static-config.json';
import { formatExpiry, validateExpiry } from './utils';
import env from "../../../../environments/production";

function useTokenxForm(_ref, ref) {
  var onTokenChange = _ref.onTokenChange;

  var _useRef = useRef(staticConfig),
      config = _useRef.current;

  var iframeRef = useRef(null);
  var expiryRef = useRef('');
  var expiryMessageRef = useRef('');
  var isCardValid = useRef(false);
  var isCvvValid = useRef(false);
  var isCvvTouched = useRef(false);
  var isCreditCardTouched = useRef(false);
  var isExpiryTouched = useRef(false);

  var _useState = useState(''),
      _useState2 = _slicedToArray(_useState, 2),
      creditCardMessage = _useState2[0],
      setCreditCardMessage = _useState2[1];

  var _useState3 = useState(''),
      _useState4 = _slicedToArray(_useState3, 2),
      cvvMessage = _useState4[0],
      setCvvMessage = _useState4[1];

  var _useState5 = useState(''),
      _useState6 = _slicedToArray(_useState5, 2),
      expiryMessage = _useState6[0],
      setExpiryMessage = _useState6[1];

  var _useState7 = useState(''),
      _useState8 = _slicedToArray(_useState7, 2),
      expiry = _useState8[0],
      setExpiry = _useState8[1];

  var loadTokenxScript = function loadTokenxScript() {
    return new Promise(function (resolve, reject) {
      try {
        var scriptEle = document.createElement("script");
        scriptEle.type = "text/javascript";
        scriptEle.async = true;
        scriptEle.src = env.tokenExIframe;
        scriptEle.addEventListener("load", function () {
          return resolve();
        });
        scriptEle.addEventListener("error", function () {
          return reject();
        });
        document.body.appendChild(scriptEle);
      } catch (e) {
        console.log(e);
        reject(e);
      }
    });
  };

  var handleTokenValidate = function handleTokenValidate(data) {
    if (data.isValid) isCreditCardTouched.current = true;
    if (data.isCvvValid) isCvvTouched.current = true;
    if (isCreditCardTouched.current) setCreditCardMessage(data.isValid ? '' : 'Please enter a valid credit card');
    if (isCvvTouched.current) setCvvMessage(data.isCvvValid ? '' : 'Please enter a valid cvv');
    isCardValid.current = data.isValid;
    isCvvValid.current = data.isCvvValid;
  };

  var addListeners = function addListeners(iframe) {
    iframe.on('validate', function (data) {
      handleTokenValidate(data);

      if (data.isValid && data.isCvvValid && !expiryMessageRef.current && expiryRef.current) {
        iframe.tokenize();
      }
    });
    iframe.on('tokenize', function (data) {
      if (expiryMessage || !isExpiryTouched.current) return;
      onTokenChange({
        cardNumber: data.token,
        cvv: data.tokenHMAC,
        expDate: expiryRef.current
      });
    });
    iframe.on('focus', function () {
      isCreditCardTouched.current = true;
    });
    iframe.on('cvvFocus', function () {
      isCvvTouched.current = true;
    });
    iframe.on("autoCompleteValues", function () {
      isCreditCardTouched.current = true;
      isCvvTouched.current = true;
      setTimeout(function () {
        handleExpiryChange({
          target: {
            value: expiryRef.current
          }
        });
        iframe.validate();
      }, 500);
    });
  };

  var handleTokenxLoad = function handleTokenxLoad() {
    var auth = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    var iframe = new TokenEx.Iframe('card-element', _objectSpread(_objectSpread({}, config), {
      authenticationKey: auth.AuthenticationKey,
      timestamp: auth.Timestamp,
      origin: auth.Origin,
      tokenScheme: auth.TokenScheme,
      tokenExID: env.tokenExId
    }));
    iframe.load();
    return iframe;
  };

  var fetchAuth = /*#__PURE__*/function () {
    var _ref2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) {
          switch (_context.prev = _context.next) {
            case 0:
              _context.prev = 0;
              _context.next = 3;
              return fetch("".concat(env.apiUrl, "/token-ex-api/v1/GetAuthToken/iframe"), {
                headers: {
                  'Ocp-Apim-Subscription-Key': env.apiSubscription,
                  'referer': window.location.origin
                }
              });

            case 3:
              _context.next = 5;
              return _context.sent.json();

            case 5:
              return _context.abrupt("return", _context.sent);

            case 8:
              _context.prev = 8;
              _context.t0 = _context["catch"](0);
              return _context.abrupt("return", console.log(_context.t0));

            case 11:
            case "end":
              return _context.stop();
          }
        }
      }, _callee, null, [[0, 8]]);
    }));

    return function fetchAuth() {
      return _ref2.apply(this, arguments);
    };
  }();

  var loadIframe = /*#__PURE__*/function () {
    var _ref3 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2() {
      var _yield$Promise$all, _yield$Promise$all2, auth, iframe;

      return _regeneratorRuntime.wrap(function _callee2$(_context2) {
        while (1) {
          switch (_context2.prev = _context2.next) {
            case 0:
              _context2.next = 2;
              return Promise.all([fetchAuth(), loadTokenxScript()]);

            case 2:
              _yield$Promise$all = _context2.sent;
              _yield$Promise$all2 = _slicedToArray(_yield$Promise$all, 1);
              auth = _yield$Promise$all2[0];
              iframe = handleTokenxLoad(auth);
              iframeRef.current = iframe;
              addListeners(iframe);

            case 8:
            case "end":
              return _context2.stop();
          }
        }
      }, _callee2);
    }));

    return function loadIframe() {
      return _ref3.apply(this, arguments);
    };
  }();

  var handleExpiryChange = function handleExpiryChange(e) {
    var formattedValue = formatExpiry(e.target.value, expiry);
    var msg = validateExpiry(formattedValue) ? '' : 'Please enter a valid expiry date';
    isExpiryTouched.current = true;
    setExpiry(formattedValue);
    setExpiryMessage(msg);
    expiryRef.current = formattedValue;
    expiryMessageRef.current = msg;
    if (!msg && isCardValid.current && isCvvValid.current) iframeRef.current.tokenize();
  };

  useImperativeHandle(ref, function () {
    return {
      validate: function validate() {
        isCreditCardTouched.current = true;
        isCvvTouched.current = true;
        handleExpiryChange({
          target: {
            value: expiryRef.current
          }
        });
        iframeRef.current.validate(function (data) {
          return data;
        });
      }
    };
  }, []);
  useEffect(function () {
    if (creditCardMessage || cvvMessage || expiryMessage) {
      onTokenChange({});
    }
  }, [creditCardMessage, cvvMessage, expiryMessage]);
  useEffect(function () {
    loadIframe();
  }, []);
  return {
    creditCardMessage: creditCardMessage,
    cvvMessage: cvvMessage,
    expiry: expiry,
    expiryMessage: expiryMessage,
    onExpiryChange: handleExpiryChange
  };
}

export default useTokenxForm;